/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {OrderApiService} from '../order/order-api.service';
import {AdvertiserApiService, AdvertiserModel} from './advertiser-api.service';
import {AdvertiserItemPageComponent} from './advertiser-item-page.component';
import {AdvertiserListPageComponent} from './advertiser-list-page.component';

export function advertiserStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(AdvertiserApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.manageAdvertising.apiKey)
    && !permissions.hasNetworkPermission(PERMISSIONS.axAdvertiserAdmin.apiKey)
    && !permissions.hasAny(PERMISSIONS.axAdvertiserTeamAdmin.apiKey);
  const rh = adnRouterHelper.createRouterAndChildHelper(
    'advertisers.advertiser', AdvertiserApiService, OrderApiService, 'app.adv', 'app'
  );
  const titleOptions = {objectTypeKey: 'Advertiser', crumbParams: []};

  states.add(rh.listPath(), rh.list({
    resolve: {
      readOnly
    },
    views: {'content@app': {component: AdvertiserListPageComponent, bindings: {readOnly: 'listReadOnly'}}}
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      advertiserPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<AdvertiserModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []},
            true, readOnly(), api, 'Advertiser', 'name', undefined, undefined, titleOptions
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, 'Advertiser', 'name', undefined, undefined, titleOptions
        ));
      },
      model: function(advertiserPage: SignalAdminResourcePageState<AdvertiserModel>) {
        return advertiserPage.model();
      }
    },
    views: {
      'content@app': {component: AdvertiserItemPageComponent, bindings: {state: 'advertiserPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'advertiserPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'advertiserPage'}}
    }
  }));
  return states.build();
}
